
import { defineComponent, ref } from "vue";

import { useApiStore } from "@/stores/apiStore";
import { useAuthStore } from "@/stores/authStore";
import { useConfigStore } from "@/stores/configStore";
import { useSiteStore } from "@/stores/siteStore";
import { useToastStore } from "@/stores/toastStore";
interface InviteRolesResponse {
	roles: Role[];
	allowedDomains: string[];
}

interface SitePermission {
	siteId: string;
	roleId: string;
}
interface SelectInputItem {
	value: string;
	text: string;
}

export default defineComponent({
	name: "imdex-invite-sso-users",
	setup() {
		return {
			api: useApiStore(),
			authStore: useAuthStore(),
			configStore: useConfigStore(),
			siteStore: useSiteStore(),
			toast: useToastStore()
		};
	},
	data() {
		return {
			emailInputErrorMessage: "",
			fetchErrorMessage: "",
			submittingInvites: false,
			submitError: false,
			submitErrorMessage: "",
			emails: [] as string[],
			newEmail: "",
			userRole: {} as SelectInputItem,
			globalRoles: [] as SelectInputItem[], // User (global) roles (name and id)
			sites: [] as SelectInputItem[], // links site name and ID
			siteRoles: [] as SelectInputItem[], // available site roles.  Will add viewer and contributor
			sitePermissions: ref<SitePermission[]>([]), // Site/role pairs to invite users
			allowedDomains: [] as string[]
		};
	},
	async mounted() {
		await this.configStore.init();
		await this.getRoles();
		await this.getAvailableSites();
		this.$nextTick(() => {
			this.focusInput();
		});
	},
	computed: {
		isInviteValid(): boolean {
			return !!(
				this.emails?.length > 0 &&
				this.userRole?.value &&
				this.sitePermissions?.length > 0
			);
		}
	},
	watch: {
		newEmail(value: string) {
			if (value === "") {
				this.emailInputErrorMessage = ""; // Clear the error state when input is empty
			}
		}
	},
	methods: {
		excludeSelectedSites(sites: SelectInputItem[], selectedSiteId: string) {
			return sites.filter((site: SelectInputItem) => {
				return (
					selectedSiteId == site.value ||
					!this.sitePermissions.find((sp) => sp.siteId === site.value)
				);
			});
		},
		close() {
			this.$emit("close");
		},
		addEmail() {
			this.emailInputErrorMessage = "";
			if (!this.newEmail) {
				return;
			}
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (this.emails.find((e) => e === this.newEmail)) {
				this.emailInputErrorMessage =
					`${this.newEmail} ` + this.$t("inviteSSOUsers.inListError");
			} else if (emailRegex.test(this.newEmail)) {
				const emailDomainSplit = this.newEmail.split("@");
				const emailDomain = emailDomainSplit[emailDomainSplit.length - 1];
				if (!this.allowedDomains.includes(emailDomain)) {
					this.emailInputErrorMessage = this.$t("inviteSSOUsers.invalidDomainError");
					return;
				}
				this.emails.push(this.newEmail);
				this.newEmail = "";
			} else {
				this.emailInputErrorMessage = this.$t("inviteSSOUsers.invalidEmailError");
			}
		},
		removeEmail(index: any) {
			this.emails.splice(index, 1);
		},
		focusInput() {
			if ((this.$refs as any).emailInput) {
				(this.$refs as any).emailInput.focus();
			}
		},
		async getRoles() {
			try {
				const { data }: { data: InviteRolesResponse } =
					await this.api.get("/role/get/invite-roles");

				this.allowedDomains = data.allowedDomains;

				// Find possible user roles (global == true)
				this.globalRoles = data.roles
					.filter((role: Role) => role.global)
					.map((role: Role) => {
						return {
							value: role.id,
							text: role.name
						};
					});

				// Now find the role IDs for the possible site permissions
				// Find roles for "Viewer" and "Contributor" (case-insensitive)
				// Would be much neater if we could just use siteRoles!
				this.siteRoles = data.roles
					.filter(
						(role: Role) =>
							!role.global && (role.name === "Viewer" || role.name === "Contributor")
					)
					.map((role: Role) => {
						return {
							value: role.id,
							text: role.name
						};
					});
			} catch (e) {
				this.fetchErrorMessage = this.$t("inviteSSOUsers.errorFetchingRoles");
			}
		},
		async getAvailableSites() {
			try {
				const { data } = await this.api.get(`/user/read-site-roles`);
				this.sites = data.map((site: any) => ({
					value: site.id,
					text: site.name
				}));
			} catch (e) {
				this.fetchErrorMessage = this.$t("inviteSSOUsers.errorFetchingSites");
			}
		},
		addSite() {
			let unusedSiteIndex = 0;
			for (let i = 0; i < this.sites.length; i++) {
				if (!this.sitePermissions.find((sp) => sp.siteId === this.sites[i].value)) {
					unusedSiteIndex = i;
					break;
				}
			}

			// Create a new SitePermission object with default values
			const newSitePermission: SitePermission = {
				siteId: this.sites[unusedSiteIndex].value,
				roleId: this.siteRoles[0].value
			};

			// Add the new SitePermission object to siteRoles array
			this.sitePermissions.push(newSitePermission);
		},
		removeSite(index: any) {
			this.sitePermissions.splice(index, 1);
		},
		async submitInvites() {
			this.submittingInvites = true;
			try {
				const body = {
					emails: this.emails, // List of entered emails
					globalRoleId: this.userRole?.value, // Selected user role id
					sites: this.sitePermissions // Array of { siteId, roleId } pairs
				};

				const { data } = await this.api.post({
					endpoint: "/user/invite-users",
					post: body
				});

				const failedEmails: string[] = data.failedEmails;
				const existingEmails: string[] = data.existingEmails;
				if (failedEmails?.length > 0 || existingEmails?.length > 0) {
					this.submitError = true;
					const errors: string[] = [];
					if (existingEmails.length > 0) {
						errors.push(
							this.$t("inviteSSOUsers.emailsExistError", {
								emails: existingEmails.join(", ")
							})
						);
					}
					if (failedEmails.length > 0) {
						errors.push(
							this.$t("inviteSSOUsers.failedToInviteError", {
								emails: failedEmails.join(", ")
							})
						);
					}

					// toast seems to strip newlines, so join with semicolons
					this.submitErrorMessage = errors.join("; ");
					// TODO error handling ticket will be raised
					this.toast.toast({
						title: this.$t("toastGeneralError"),
						dismissable: false,
						message: this.submitErrorMessage,
						type: "error"
					});
				} else {
					const numInvites = this.emails.length;

					const msg = this.$tc("inviteSSOUsers.inviteSuccessToastMsg", numInvites, {
						numInvites
					});
					this.toast.toast({
						title: this.$t("inviteSSOUsers.inviteSuccessToastTitle"),
						dismissable: false,
						message: msg,
						type: "success"
					});
				}

				this.close();
			} catch (error: any) {
				console.error("Error submitting invites:", error);
				this.submitError = true;
				this.submitErrorMessage =
					error.message || this.$t("inviteSSOUsers.inviteUnexpectedError"); // Exception is for sass compilation errors
				this.toast.toast({
					title: this.$t("toastGeneralError"),
					dismissable: false,
					message: this.submitErrorMessage,
					type: "error"
				});
				// Don't close - let user try again
			} finally {
				this.submittingInvites = false;
			}
		}
	}
});
